/**
 * AppProviders — Shared Provider Stack
 *
 * Wraps the app with TanStack Query, image config, and auth context.
 * Used inside the route tree (root-layout.tsx), below ConfigProvider.
 *
 * Config is consumed via useConfig() — never reads process.env.
 * On the server, ConfigProvider is set in render.tsx with the
 * startup-validated config. On the client, it's set in entry-client.tsx
 * from window.__CONFIG__ (serialized by the server).
 *
 * QueryClient lifecycle:
 *   Server: new instance per request (prevents cross-request data leakage)
 *   Client: module-level singleton (persists for the browser session)
 */
import { ImageConfigProvider } from '@letsgethome/ui/s3-image';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { AuthProvider } from './AuthProvider';
import { useConfig } from './ConfigProvider';
import { PublicPagerChromeProvider } from './PagerChromeProvider';
import { PublicRealtimeProvider } from './PublicRealtimeProvider';

const QUERY_DEFAULTS = {
  queries: {
    retry: 3,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  },
} as const;

function makeQueryClient() {
  return new QueryClient({ defaultOptions: QUERY_DEFAULTS });
}

let browserQueryClient: QueryClient | null = null;

function getQueryClient(): QueryClient {
  if (typeof window === 'undefined') {
    return makeQueryClient();
  }
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }
  return browserQueryClient;
}

interface AppProvidersProps {
  children: ReactNode;
}

export function AppProviders({ children }: AppProvidersProps) {
  const queryClient = getQueryClient();
  const config = useConfig();

  return (
    <QueryClientProvider client={queryClient}>
      <ImageConfigProvider imagesUrl={config.imagesUrl} s3Bucket={config.s3Bucket}>
        <AuthProvider googleClientId={config.googleClientId || undefined}>
          <PublicRealtimeProvider>
            <PublicPagerChromeProvider>{children}</PublicPagerChromeProvider>
          </PublicRealtimeProvider>
        </AuthProvider>
      </ImageConfigProvider>
    </QueryClientProvider>
  );
}
